import {useState} from "react";
import {Check, ChevronsUpDown, X} from "lucide-react";
import {useMembersTranslation} from '@/i18n/hooks';
import {ServiceGroup, ServiceWithGroupId} from "@/models/service";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,} from "@/components/ui/command";
import {Popover, PopoverContent, PopoverTrigger,} from "@/components/ui/popover";
import {Text} from "@/components/tailwind/text.tsx";

type ServiceSelectorProps = {
    services: ServiceWithGroupId[];
    groups: ServiceGroup[];
    selectedServiceIds: string[];
    onChange: (serviceIds: string[]) => void;
    placeholder?: string;
    className?: string;
    singleSelect?: boolean;
    'data-testid'?: string;
};

function ServiceSelector({
                             services,
                             groups,
                             selectedServiceIds,
                             onChange,
                             placeholder = "Select service...",
                             className,
                             'data-testid': dataTestId,
                         }: ServiceSelectorProps) {
    const {t} = useMembersTranslation();
    const [open, setOpen] = useState(false);

    const displayedServices = services.filter(service =>
        selectedServiceIds.includes(service.id)
    );

    const availableServices = services.filter(
        service => !selectedServiceIds.includes(service.id)
    );

    const servicesByGroup = groups.map(group => {
        const servicesInGroup = availableServices.filter(service =>
            service.serviceGroupId === group.id
        );
        return {
            group,
            services: servicesInGroup
        };
    }).filter(group => group.services.length > 0);

    const ungroupedServices = availableServices.filter(service =>
        !service.serviceGroupId
    );

    function addService(service: ServiceWithGroupId) {
        onChange([...selectedServiceIds, service.id]);
        setOpen(false);
    }

    function removeService(serviceId: string) {
        onChange(selectedServiceIds.filter(id => id !== serviceId));
    }

    return (
        <div className="space-y-4">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            "w-full justify-between font-normal",
                            !selectedServiceIds.length && "text-muted-foreground",
                            className
                        )}
                        data-testid={dataTestId}
                    >
                        <Text>{placeholder}</Text>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                    <Command>
                        <CommandInput placeholder={t("memberForm.fields.services.searchPlaceholder")}/>
                        <CommandEmpty>{t("memberForm.fields.services.noOptions")}</CommandEmpty>
                        <CommandList>
                            {servicesByGroup.map(({group, services}) => (
                                <CommandGroup key={group.id} heading={group.name}>
                                    {services
                                        .filter((service) => !service.isDeleted)
                                        .map((service) => (
                                            <CommandItem
                                                key={service.id}
                                                value={service.name}
                                                onSelect={() => addService(service)}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        selectedServiceIds.includes(service.id)
                                                            ? "opacity-100"
                                                            : "opacity-0"
                                                    )}
                                                />
                                                {service.name}
                                            </CommandItem>
                                        ))}
                                </CommandGroup>
                            ))}

                            {ungroupedServices.length > 0 && (
                                <CommandGroup heading={t("memberForm.fields.services.ungrouped")}>
                                    {ungroupedServices
                                        .filter((service) => !service.isDeleted)
                                        .map((service) => (
                                            <CommandItem
                                                key={service.id}
                                                value={service.name}
                                                onSelect={() => addService(service)}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        selectedServiceIds.includes(service.id)
                                                            ? "opacity-100"
                                                            : "opacity-0"
                                                    )}
                                                />
                                                {service.name}
                                            </CommandItem>
                                        ))}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {displayedServices.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {displayedServices.map((service) => (
                        <div
                            key={service.id}
                            className={`rounded-full py-1 px-3 text-sm flex items-center ${
                                service.isDeleted
                                    ? "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                    : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100"
                            }`}
                        >
                            <span>{service.name}</span>
                            <button
                                type="button"
                                onClick={() => removeService(service.id)}
                                className={`ml-1.5 hover:opacity-80 ${
                                    service.isDeleted
                                        ? "text-gray-700 dark:text-gray-300"
                                        : "text-blue-800 dark:text-blue-200"
                                }`}
                                aria-label={`${service.name} entfernen`}
                            >
                                <X className="h-3 w-3"/>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default ServiceSelector;
